import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, Cell, PieChart, Pie, Legend,
} from "recharts";
import {
  ArrowLeft, Download, ThumbsUp, ThumbsDown, TrendingUp, TrendingDown,
  Sparkles, MessageSquare, Quote,
} from "lucide-react";
import { stripHtml } from "@/components/RichText";

/* Brand palette */
const C = {
  brand: "#F58220",
  accent: "#F58220",
  promoter: "#10B981",
  passive: "#F59E0B",
  detractor: "#EF4444",
  slate: "#94A3B8",
};

const CAT_COLOR = { promoter: C.promoter, passive: C.passive, detractor: C.detractor };
const CAT_LABEL = { promoter: "Promoters", passive: "Passives", detractor: "Detractors" };

export default function SurveyAnalyticsPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [responses, setResponses] = useState([]);
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    api.get(`/surveys/${id}/analytics`).then((r) => setData(r.data));
    api.get(`/surveys/${id}/responses`).then((r) => setResponses(r.data));
  }, [id]);

  if (!data) return <div className="text-slate-500">Loading…</div>;
  const { totals, trend, questions, survey, insights = [], temporal = {} } = data;
  const titlePlain = stripHtml(survey.title) || "Untitled";

  const exportCSV = () => {
    if (!responses.length) return;
    const qids = (survey.questions || []).map((q) => q.id);
    const headers = ["submitted_at", "nps_score", "nps_category", ...qids.map((qid) => {
      const q = survey.questions.find((qq) => qq.id === qid);
      return q ? stripHtml(q.title).replace(/,/g, " ") : qid;
    })];
    const rows = responses.map((r) => {
      const map = {};
      r.answers.forEach((a) => { map[a.question_id] = a.value; });
      return [r.submitted_at, r.nps_score ?? "", r.nps_category ?? "", ...qids.map((qid) => {
        const v = map[qid];
        if (Array.isArray(v)) return `"${v.join("|")}"`;
        if (typeof v === "string") return `"${v.replace(/"/g, '""')}"`;
        return v ?? "";
      })].join(",");
    });
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${titlePlain}-responses.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5 fade-in">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link to="/app/surveys"><Button variant="ghost" size="icon"><ArrowLeft size={18}/></Button></Link>
          <div>
            <h2 className="text-xl font-extrabold text-[#1F3A6B]">{titlePlain}</h2>
            <p className="text-sm text-slate-500">Analytics & insights</p>
          </div>
        </div>
        <Button data-testid="analytics-export" onClick={exportCSV} variant="outline"><Download size={16} className="mr-1.5"/>Export CSV</Button>
      </div>

      {/* KPI strip */}
      <div className="grid md:grid-cols-5 gap-3">
        <KpiCard data-testid="ana-nps" label="NPS Score" value={totals.nps_score} hint={npsZone(totals.nps_score)} tone="brand"/>
        <KpiCard data-testid="ana-responses" label="Responses" value={totals.responses} tone="cyan"/>
        <KpiCard data-testid="ana-promoters" label="Promoters" value={totals.promoters} sub={`${pct(totals.promoters, totals.responses)}%`} tone="emerald"/>
        <KpiCard data-testid="ana-passives" label="Passives" value={totals.passives} sub={`${pct(totals.passives, totals.responses)}%`} tone="amber"/>
        <KpiCard data-testid="ana-detractors" label="Detractors" value={totals.detractors} sub={`${pct(totals.detractors, totals.responses)}%`} tone="rose"/>
      </div>

      {totals.weighted_score !== undefined && totals.weighted_score !== null && (
        <Card className="p-5 border-[#FFE1BE] bg-[#FFF0DF]/40">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-wider text-[#F58220] font-bold">Weighted score</p>
              <p className="text-3xl font-extrabold text-[#1F3A6B] tabular-nums">{totals.weighted_score} / 100</p>
            </div>
            <p className="text-xs text-slate-600 max-w-md">Computed from questions with assigned weights. Each numeric question is normalized to 0–100 then weight-averaged.</p>
          </div>
        </Card>
      )}

      {/* AI-style insights */}
      {insights.length > 0 && (
        <Card className="p-5 border-slate-100" data-testid="ana-insights">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-[#F58220]"/>
            <p className="font-bold text-[#1F3A6B]">Auto insights</p>
            <Badge variant="outline" className="text-[10px] uppercase bg-[#FFF0DF] text-[#F58220] border-[#FFE1BE]">{insights.length}</Badge>
          </div>
          <ul className="mt-3 grid md:grid-cols-2 gap-3">
            {insights.slice(0, 6).map((ins, i) => (
              <InsightCard key={i} ins={ins}/>
            ))}
          </ul>
        </Card>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-100 overflow-x-auto no-scrollbar">
        {[
          { id: "overview", label: "Overview" },
          { id: "questions", label: "Per-question" },
          { id: "drivers", label: "Drivers" },
          { id: "voice", label: "Voice of customer" },
          { id: "patterns", label: "Patterns" },
          { id: "responses", label: `Responses (${responses.length})` },
        ].map((t) => (
          <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px whitespace-nowrap transition-colors ${tab === t.id ? "border-[#F58220] text-[#1F3A6B]" : "border-transparent text-slate-500 hover:text-[#1F3A6B]"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && <OverviewTab data={data}/>}
      {tab === "questions" && <QuestionsTab questions={questions}/>}
      {tab === "drivers" && <DriversTab questions={questions} totals={totals}/>}
      {tab === "voice" && <VoiceTab questions={questions} responses={responses} survey={survey}/>}
      {tab === "patterns" && <PatternsTab temporal={temporal} trend={trend}/>}
      {tab === "responses" && <ResponsesTab responses={responses} survey={survey}/>}
    </div>
  );
}

/* ---------- OVERVIEW ---------- */
function OverviewTab({ data }) {
  const { trend, totals } = data;
  const dist = [
    { name: "Promoters", value: totals.promoters, color: C.promoter },
    { name: "Passives", value: totals.passives, color: C.passive },
    { name: "Detractors", value: totals.detractors, color: C.detractor },
  ];
  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <Card className="p-5 border-slate-100 lg:col-span-2">
        <h3 className="font-bold text-[#1F3A6B]">NPS Trend</h3>
        <div className="h-72 mt-3">
          {trend.length === 0 ? (
            <Empty msg="No responses yet — share the survey link to start collecting feedback."/>
          ) : (
            <ResponsiveContainer>
              <AreaChart data={trend.map((t) => ({ ...t, label: t.date.slice(5) }))}>
                <defs>
                  <linearGradient id="ag" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0" stopColor={C.accent} stopOpacity="0.4"/>
                    <stop offset="1" stopColor={C.accent} stopOpacity="0"/>
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#eef2f7" vertical={false}/>
                <XAxis dataKey="label" tickLine={false} axisLine={false}/>
                <YAxis domain={[-100, 100]} tickLine={false} axisLine={false} width={32}/>
                <Tooltip/>
                <Area type="monotone" dataKey="nps" stroke={C.accent} strokeWidth={2.4} fill="url(#ag)"/>
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </Card>
      <Card className="p-5 border-slate-100">
        <h3 className="font-bold text-[#1F3A6B]">Mix</h3>
        <div className="h-72 mt-3">
          {totals.responses === 0 ? <Empty msg="No responses yet."/> : (
            <ResponsiveContainer>
              <PieChart>
                <Pie data={dist} dataKey="value" nameKey="name" innerRadius={50} outerRadius={88} paddingAngle={2}>
                  {dist.map((d, i) => <Cell key={i} fill={d.color}/>)}
                </Pie>
                <Legend/>
                <Tooltip/>
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
        <NpsBar totals={totals}/>
      </Card>
    </div>
  );
}

function NpsBar({ totals }) {
  const tot = totals.responses || 1;
  const segs = [
    { key: "detractor", value: totals.detractors, color: C.detractor },
    { key: "passive", value: totals.passives, color: C.passive },
    { key: "promoter", value: totals.promoters, color: C.promoter },
  ];
  return (
    <div className="mt-3">
      <div className="flex h-3 rounded-full overflow-hidden bg-slate-100">
        {segs.map((s) => (
          <div key={s.key} title={`${CAT_LABEL[s.key]}: ${s.value}`} style={{ width: `${(s.value / tot) * 100}%`, backgroundColor: s.color }}/>
        ))}
      </div>
      <div className="mt-1 flex justify-between text-[11px] text-slate-500">
        <span className="text-rose-600">{pct(totals.detractors, tot)}% Detractors</span>
        <span className="text-amber-600">{pct(totals.passives, tot)}% Passives</span>
        <span className="text-emerald-600">{pct(totals.promoters, tot)}% Promoters</span>
      </div>
    </div>
  );
}

/* ---------- PER-QUESTION (smart visuals) ---------- */
function QuestionsTab({ questions }) {
  if (!questions.length) return <Empty msg="No questions yet."/>;
  return (
    <div className="grid md:grid-cols-2 gap-4">
      {questions.map((q) => <QuestionCard key={q.question_id} q={q}/>)}
    </div>
  );
}

function QuestionCard({ q }) {
  const title = stripHtml(q.title);
  return (
    <Card className="p-5 border-slate-100" data-testid={`q-card-${q.question_id}`}>
      <div className="flex items-start justify-between gap-3">
        <p className="font-semibold text-[#1F3A6B] line-clamp-2">{title}</p>
        <Badge variant="outline" className="text-[10px] uppercase bg-[#FFF0DF] text-[#F58220] border-[#FFE1BE] shrink-0">{q.type}</Badge>
      </div>
      <p className="text-xs text-slate-500 mt-1">
        {q.count} response{q.count === 1 ? "" : "s"}
        {q.average !== undefined ? <> · avg <span className="font-semibold text-[#1F3A6B]">{q.average}</span></> : null}
      </p>
      <div className="mt-3">
        <SmartChart q={q}/>
      </div>
    </Card>
  );
}

function SmartChart({ q }) {
  if (q.type === "nps") return <NpsHistogram distribution={q.distribution}/>;
  if (q.type === "csat" || q.type === "ces" || q.type === "rating" || q.type === "slider") {
    return <NumericHistogram distribution={q.distribution}/>;
  }
  if (q.type === "yes_no") return <YesNoChart counts={q.counts}/>;
  if (q.type === "emoji") return <EmojiChart counts={q.counts} options={extractOptions(q.counts)}/>;
  if (q.type === "mcq" || q.type === "dropdown") return <HBarChart counts={q.counts} color={C.brand}/>;
  if (q.type === "multi_select") return <HBarChart counts={q.counts} color={C.accent}/>;
  if (q.type === "ranking") return <HBarChart counts={q.counts} color={C.brand}/>;
  if (q.type === "image_choice") return <HBarChart counts={q.counts} color={C.brand}/>;
  if (q.type === "long_text" || q.type === "text") return <TopWords words={q.top_words || []}/>;
  return <p className="text-sm text-slate-500">No chart for this type.</p>;
}

function extractOptions(counts) { return Object.keys(counts || {}); }

function NpsHistogram({ distribution }) {
  if (!distribution) return <Empty msg="No responses."/>;
  const data = [];
  for (let i = 0; i <= 10; i++) {
    data.push({ k: String(i), v: distribution[String(i)] || 0 });
  }
  return (
    <ResponsiveContainer width="100%" height={170}>
      <BarChart data={data}>
        <CartesianGrid stroke="#eef2f7" vertical={false}/>
        <XAxis dataKey="k" tickLine={false} axisLine={false}/>
        <YAxis tickLine={false} axisLine={false} width={28} allowDecimals={false}/>
        <Tooltip/>
        <Bar dataKey="v" radius={[4, 4, 0, 0]}>
          {data.map((d, i) => {
            const n = Number(d.k);
            const fill = n <= 6 ? C.detractor : n <= 8 ? C.passive : C.promoter;
            return <Cell key={i} fill={fill}/>;
          })}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function NumericHistogram({ distribution }) {
  if (!distribution) return <Empty msg="No responses."/>;
  const keys = Object.keys(distribution).sort((a, b) => Number(a) - Number(b));
  const data = keys.map((k) => ({ k, v: distribution[k] }));
  return (
    <ResponsiveContainer width="100%" height={170}>
      <BarChart data={data}>
        <CartesianGrid stroke="#eef2f7" vertical={false}/>
        <XAxis dataKey="k" tickLine={false} axisLine={false}/>
        <YAxis tickLine={false} axisLine={false} width={28} allowDecimals={false}/>
        <Tooltip/>
        <Bar dataKey="v" fill={C.accent} radius={[4, 4, 0, 0]}/>
      </BarChart>
    </ResponsiveContainer>
  );
}

function YesNoChart({ counts }) {
  const yes = counts?.yes || 0;
  const no = counts?.no || 0;
  const total = yes + no || 1;
  if (yes === 0 && no === 0) return <Empty msg="No responses."/>;
  return (
    <div className="flex items-center gap-4 py-3">
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <ThumbsUp size={18} className="text-emerald-600 fill-emerald-600"/>
          <span className="text-sm font-semibold text-emerald-700">Yes</span>
          <span className="ml-auto text-sm font-bold tabular-nums">{yes}</span>
          <span className="text-xs text-slate-500 tabular-nums">({pct(yes, total)}%)</span>
        </div>
        <div className="mt-1.5 h-2 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-emerald-500" style={{ width: `${(yes / total) * 100}%` }}/>
        </div>
        <div className="mt-3 flex items-center gap-2">
          <ThumbsDown size={18} className="text-rose-600 fill-rose-600"/>
          <span className="text-sm font-semibold text-rose-700">No</span>
          <span className="ml-auto text-sm font-bold tabular-nums">{no}</span>
          <span className="text-xs text-slate-500 tabular-nums">({pct(no, total)}%)</span>
        </div>
        <div className="mt-1.5 h-2 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-rose-500" style={{ width: `${(no / total) * 100}%` }}/>
        </div>
      </div>
    </div>
  );
}

function EmojiChart({ counts }) {
  const opts = Object.keys(counts || {});
  if (!opts.length) return <Empty msg="No responses."/>;
  const total = opts.reduce((a, k) => a + counts[k], 0) || 1;
  return (
    <div className="space-y-2">
      {opts.map((o) => {
        const isUp = o === "👍";
        const isDown = o === "👎";
        const isRed = o === "🔴";
        const isYellow = o === "🟡";
        const isGreen = o === "🟢";
        const color = isUp || isGreen ? C.promoter
          : isDown || isRed ? C.detractor
          : isYellow ? C.passive
          : C.brand;
        const v = counts[o];
        const w = (v / total) * 100;
        return (
          <div key={o} className="flex items-center gap-3">
            <div className="w-8 grid place-items-center shrink-0">
              {isUp ? <ThumbsUp size={18} className="text-emerald-600 fill-emerald-600"/>
                : isDown ? <ThumbsDown size={18} className="text-rose-600 fill-rose-600"/>
                : isRed ? <span className="w-4 h-4 rounded-full bg-rose-500"/>
                : isYellow ? <span className="w-4 h-4 rounded-full bg-amber-400"/>
                : isGreen ? <span className="w-4 h-4 rounded-full bg-emerald-500"/>
                : <span className="text-lg">{o}</span>}
            </div>
            <div className="flex-1">
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full" style={{ width: `${w}%`, backgroundColor: color }}/>
              </div>
            </div>
            <div className="text-xs tabular-nums text-slate-600 w-20 text-right">
              <span className="font-bold text-[#1F3A6B]">{v}</span> <span className="text-slate-400">({Math.round(w)}%)</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function HBarChart({ counts, color }) {
  const entries = Object.entries(counts || {}).sort((a, b) => b[1] - a[1]);
  if (!entries.length) return <Empty msg="No responses."/>;
  const total = entries.reduce((a, [, v]) => a + v, 0) || 1;
  return (
    <div className="space-y-2">
      {entries.map(([k, v]) => {
        const w = (v / total) * 100;
        return (
          <div key={k} className="flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-xs text-slate-700 truncate">{k}</p>
              <div className="mt-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full" style={{ width: `${w}%`, backgroundColor: color }}/>
              </div>
            </div>
            <div className="text-xs tabular-nums text-slate-600 w-20 text-right">
              <span className="font-bold text-[#1F3A6B]">{v}</span> <span className="text-slate-400">({Math.round(w)}%)</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TopWords({ words }) {
  if (!words || words.length === 0) return <Empty msg="No text responses yet."/>;
  const max = Math.max(...words.map((w) => w.count));
  return (
    <div className="flex flex-wrap gap-1.5">
      {words.map((w, i) => {
        const ratio = w.count / max;
        const fontSize = 11 + Math.round(ratio * 12); // 11..23 px
        const opacity = 0.55 + ratio * 0.45;
        return (
          <span key={i} className="font-bold tracking-tight" style={{ fontSize, color: C.brand, opacity }}>
            {w.word}
          </span>
        );
      })}
    </div>
  );
}

/* ---------- DRIVERS ---------- */
function DriversTab({ questions, totals }) {
  const driverQs = questions.filter((q) => q.drivers && Object.keys(q.drivers).length > 0);
  if (!driverQs.length) return <Empty msg="No driver data yet — drivers compare answers across NPS categories."/>;
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">How each option splits across <span className="text-emerald-600 font-bold">Promoters</span>, <span className="text-amber-600 font-bold">Passives</span>, and <span className="text-rose-600 font-bold">Detractors</span>. Bigger red bars = bigger problems.</p>
      {driverQs.map((q) => <DriverCard key={q.question_id} q={q} totals={totals}/>)}
    </div>
  );
}

function DriverCard({ q, totals }) {
  const title = stripHtml(q.title);
  const data = useMemo(() => {
    const arr = Object.entries(q.drivers).map(([opt, row]) => ({
      option: opt,
      promoter: row.promoter || 0,
      passive: row.passive || 0,
      detractor: row.detractor || 0,
      total: (row.promoter || 0) + (row.passive || 0) + (row.detractor || 0),
    }));
    return arr.sort((a, b) => b.detractor - a.detractor || b.total - a.total);
  }, [q]);

  const isThumbsOrLight = (opt) => ["👍", "👎", "🔴", "🟡", "🟢"].includes(opt);
  const renderOptIcon = (o) => {
    if (o === "👍") return <ThumbsUp size={14} className="text-emerald-600 fill-emerald-600"/>;
    if (o === "👎") return <ThumbsDown size={14} className="text-rose-600 fill-rose-600"/>;
    if (o === "🔴") return <span className="w-3 h-3 rounded-full bg-rose-500"/>;
    if (o === "🟡") return <span className="w-3 h-3 rounded-full bg-amber-400"/>;
    if (o === "🟢") return <span className="w-3 h-3 rounded-full bg-emerald-500"/>;
    return null;
  };

  return (
    <Card className="p-5 border-slate-100" data-testid={`driver-card-${q.question_id}`}>
      <div className="flex items-start justify-between gap-3">
        <p className="font-semibold text-[#1F3A6B]">{title}</p>
        <Badge variant="outline" className="text-[10px] uppercase bg-[#FFF0DF] text-[#F58220] border-[#FFE1BE] shrink-0">{q.type}</Badge>
      </div>
      <ResponsiveContainer width="100%" height={Math.max(180, data.length * 38 + 40)} className="mt-3">
        <BarChart data={data} layout="vertical" stackOffset="expand" margin={{ left: 8 }}>
          <CartesianGrid stroke="#eef2f7" horizontal={false}/>
          <XAxis type="number" tickFormatter={(v) => `${Math.round(v * 100)}%`} tickLine={false} axisLine={false}/>
          <YAxis type="category" dataKey="option" tickLine={false} axisLine={false} width={140}
            tick={({ x, y, payload }) => (
              <g transform={`translate(${x},${y})`}>
                {isThumbsOrLight(payload.value)
                  ? <foreignObject x={-130} y={-9} width={120} height={18}>
                      <div className="flex items-center gap-1.5 text-[11px] text-slate-700 truncate">
                        {renderOptIcon(payload.value)}
                        <span className="truncate">{payload.value}</span>
                      </div>
                    </foreignObject>
                  : <text x={-6} y={4} textAnchor="end" fill="#475569" fontSize={11}>
                      {String(payload.value).length > 18 ? String(payload.value).slice(0, 17) + "…" : payload.value}
                    </text>
                }
              </g>
            )}/>
          <Tooltip formatter={(v) => v} labelFormatter={(l) => `Option: ${l}`}/>
          <Bar dataKey="detractor" stackId="a" fill={C.detractor} name="Detractors" radius={[0, 0, 0, 0]}/>
          <Bar dataKey="passive" stackId="a" fill={C.passive} name="Passives"/>
          <Bar dataKey="promoter" stackId="a" fill={C.promoter} name="Promoters" radius={[0, 4, 4, 0]}/>
        </BarChart>
      </ResponsiveContainer>
      <div className="mt-2 flex items-center gap-3 text-[11px] text-slate-500">
        <LegendDot color={C.detractor}/> Detractor share
        <LegendDot color={C.passive}/> Passive share
        <LegendDot color={C.promoter}/> Promoter share
      </div>
    </Card>
  );
}

function LegendDot({ color }) {
  return <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }}/>;
}

/* ---------- VOICE OF CUSTOMER ---------- */
function VoiceTab({ questions, responses, survey }) {
  const textQs = questions.filter((q) => q.type === "long_text" || q.type === "text");
  if (!textQs.length) return <Empty msg="No open-ended questions in this survey."/>;

  // Find an NPS question id (if any) to color quotes
  const npsQ = (survey.questions || []).find((q) => q.type === "nps");
  const respByCat = (cat) => responses.filter((r) => r.nps_category === cat);

  return (
    <div className="space-y-4">
      {textQs.map((q) => {
        // Collect comments per NPS category
        const buckets = { promoter: [], passive: [], detractor: [] };
        for (const r of responses) {
          const a = r.answers.find((aa) => aa.question_id === q.question_id);
          if (!a || typeof a.value !== "string" || !a.value.trim()) continue;
          const cat = r.nps_category || "passive";
          if (buckets[cat]) buckets[cat].push({ text: a.value, score: r.nps_score, when: r.submitted_at });
        }
        return (
          <Card key={q.question_id} className="p-5 border-slate-100" data-testid={`voc-${q.question_id}`}>
            <div className="flex items-center justify-between">
              <p className="font-bold text-[#1F3A6B]">{stripHtml(q.title)}</p>
              <Badge variant="outline" className="text-[10px] uppercase bg-[#FFF0DF] text-[#F58220] border-[#FFE1BE]">{q.count} comments</Badge>
            </div>

            {q.top_words?.length > 0 && (
              <div className="mt-3 pb-3 border-b border-slate-100">
                <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Top themes</p>
                <TopWords words={q.top_words}/>
              </div>
            )}

            <div className="mt-3 grid md:grid-cols-3 gap-3">
              <QuoteList title="Detractors" tone="rose" items={buckets.detractor}/>
              <QuoteList title="Passives" tone="amber" items={buckets.passive}/>
              <QuoteList title="Promoters" tone="emerald" items={buckets.promoter}/>
            </div>
          </Card>
        );
      })}
    </div>
  );
}

function QuoteList({ title, tone, items }) {
  const toneClass = {
    rose: "border-rose-200 bg-rose-50/50",
    amber: "border-amber-200 bg-amber-50/50",
    emerald: "border-emerald-200 bg-emerald-50/50",
  }[tone];
  const headTone = {
    rose: "text-rose-700",
    amber: "text-amber-700",
    emerald: "text-emerald-700",
  }[tone];
  return (
    <div>
      <p className={`text-xs uppercase tracking-wider font-bold ${headTone}`}>{title} <span className="text-slate-400 font-medium">({items.length})</span></p>
      <ul className="mt-2 space-y-2 max-h-72 overflow-auto pr-1">
        {items.length === 0
          ? <li className="text-xs text-slate-400 italic">No comments.</li>
          : items.slice(0, 12).map((it, i) => (
            <li key={i} className={`text-sm border rounded-md p-2.5 ${toneClass}`}>
              <div className="flex items-start gap-1.5">
                <Quote size={12} className="text-slate-400 mt-1 shrink-0"/>
                <p className="text-slate-700 leading-snug">{it.text}</p>
              </div>
              {it.score !== null && it.score !== undefined && (
                <p className="text-[10px] text-slate-500 mt-1 text-right">NPS: <span className="font-bold tabular-nums">{it.score}</span></p>
              )}
            </li>
          ))}
      </ul>
    </div>
  );
}

/* ---------- PATTERNS (heatmaps) ---------- */
function PatternsTab({ temporal, trend }) {
  const dow = temporal?.by_dow || [0, 0, 0, 0, 0, 0, 0];
  const hour = temporal?.by_hour || Array(24).fill(0);
  const dowMax = Math.max(1, ...dow);
  const hourMax = Math.max(1, ...hour);
  const dowLabels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <Card className="p-5 border-slate-100">
        <h3 className="font-bold text-[#1F3A6B]">Responses by day of week</h3>
        <p className="text-xs text-slate-500 mt-1">When do your customers respond most?</p>
        <div className="mt-4 grid grid-cols-7 gap-2">
          {dow.map((c, i) => {
            const ratio = c / dowMax;
            return (
              <div key={i} className="flex flex-col items-center gap-1">
                <div className="text-[11px] font-semibold text-slate-600">{dowLabels[i]}</div>
                <div className="w-full h-24 rounded-md flex items-end justify-center bg-slate-50 overflow-hidden">
                  <div className="w-full transition-all" style={{ height: `${Math.max(4, ratio * 100)}%`, backgroundColor: C.accent, opacity: 0.45 + ratio * 0.55 }}/>
                </div>
                <div className="text-[10px] tabular-nums font-bold text-[#1F3A6B]">{c}</div>
              </div>
            );
          })}
        </div>
      </Card>

      <Card className="p-5 border-slate-100">
        <h3 className="font-bold text-[#1F3A6B]">Responses by hour of day</h3>
        <p className="text-xs text-slate-500 mt-1">Peak hours guide when to send invitations.</p>
        <div className="mt-4 grid grid-cols-12 gap-1">
          {hour.map((c, h) => {
            const ratio = c / hourMax;
            return (
              <div key={h} className="flex flex-col items-center gap-0.5">
                <div className="text-[9px] text-slate-500 font-mono">{String(h).padStart(2, "0")}</div>
                <div className="w-full h-20 rounded-sm flex items-end justify-center bg-slate-50 overflow-hidden">
                  <div className="w-full transition-all" style={{ height: `${Math.max(3, ratio * 100)}%`, backgroundColor: C.brand, opacity: 0.35 + ratio * 0.65 }}/>
                </div>
                <div className="text-[9px] tabular-nums text-slate-600">{c}</div>
              </div>
            );
          })}
        </div>
      </Card>

      <Card className="p-5 border-slate-100 lg:col-span-2">
        <h3 className="font-bold text-[#1F3A6B]">Daily volume vs NPS</h3>
        <div className="h-72 mt-3">
          {trend.length === 0 ? <Empty msg="No data yet."/> : (
            <ResponsiveContainer>
              <BarChart data={trend.map((t) => ({ ...t, label: t.date.slice(5) }))}>
                <CartesianGrid stroke="#eef2f7" vertical={false}/>
                <XAxis dataKey="label" tickLine={false} axisLine={false}/>
                <YAxis tickLine={false} axisLine={false} width={28} allowDecimals={false}/>
                <Tooltip/>
                <Bar dataKey="detractors" stackId="a" fill={C.detractor}/>
                <Bar dataKey="passives" stackId="a" fill={C.passive}/>
                <Bar dataKey="promoters" stackId="a" fill={C.promoter}/>
                <Legend/>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </Card>
    </div>
  );
}

/* ---------- RESPONSES TABLE ---------- */
function ResponsesTab({ responses, survey }) {
  const [filter, setFilter] = useState("all");
  const filtered = responses.filter((r) => filter === "all" || r.nps_category === filter);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-xs text-slate-500 mr-2">Filter:</span>
        {["all", "promoter", "passive", "detractor"].map((f) => (
          <button
            key={f}
            data-testid={`response-filter-${f}`}
            onClick={() => setFilter(f)}
            className={`px-3 py-1 rounded-full text-xs font-semibold border transition ${filter === f ? "bg-[#F58220] text-white border-[#F58220]" : "bg-white border-slate-200 text-slate-600 hover:border-[#F58220]"}`}
          >
            {f === "all" ? `All (${responses.length})` : `${CAT_LABEL[f]} (${responses.filter((r) => r.nps_category === f).length})`}
          </button>
        ))}
      </div>
      <Card className="border-slate-100 overflow-x-auto">
        {filtered.length === 0 ? (
          <div className="p-10 text-center text-slate-500 text-sm">No responses match this filter.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">When</th>
                <th className="text-left px-4 py-3 font-semibold">NPS</th>
                <th className="text-left px-4 py-3 font-semibold">Category</th>
                <th className="text-left px-4 py-3 font-semibold">Email</th>
                <th className="text-left px-4 py-3 font-semibold">Comment</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const comment = r.answers.find((a) => {
                  const q = survey.questions.find((qq) => qq.id === a.question_id);
                  return q && (q.type === "long_text" || q.type === "text");
                });
                return (
                  <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/40">
                    <td className="px-4 py-3 whitespace-nowrap text-slate-700">{new Date(r.submitted_at).toLocaleString()}</td>
                    <td className="px-4 py-3 font-semibold tabular-nums">{r.nps_score ?? "—"}</td>
                    <td className="px-4 py-3"><CatBadge cat={r.nps_category}/></td>
                    <td className="px-4 py-3 text-slate-700">{r.respondent_email || "—"}</td>
                    <td className="px-4 py-3 text-slate-700 max-w-md truncate">{typeof comment?.value === "string" ? comment.value : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

/* ---------- Misc ---------- */
function KpiCard({ label, value, sub, hint, tone, ...rest }) {
  const toneClass = {
    brand: "text-[#1F3A6B]",
    cyan: "text-[#F58220]",
    emerald: "text-emerald-600",
    amber: "text-amber-600",
    rose: "text-rose-600",
  }[tone] || "text-[#1F3A6B]";
  return (
    <Card {...rest} className="p-5 border-slate-100">
      <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</p>
      <p className={`mt-2 text-3xl font-extrabold tabular-nums ${toneClass}`}>{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
      {hint && <p className={`text-[11px] font-semibold mt-1 ${toneClass}`}>{hint}</p>}
    </Card>
  );
}

function CatBadge({ cat }) {
  if (!cat) return <span className="text-slate-400">—</span>;
  const map = { promoter: "bg-emerald-100 text-emerald-700", passive: "bg-amber-100 text-amber-700", detractor: "bg-rose-100 text-rose-700" };
  return <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${map[cat]}`}>{cat}</span>;
}

function InsightCard({ ins }) {
  const isDet = ins.type === "detractor_driver";
  const Icon = isDet ? TrendingDown : TrendingUp;
  const tone = isDet ? "rose" : "emerald";
  const toneClass = isDet ? "border-rose-100 bg-rose-50/40 text-rose-700" : "border-emerald-100 bg-emerald-50/40 text-emerald-700";
  const iconColor = isDet ? "text-rose-600" : "text-emerald-600";
  return (
    <li className={`border rounded-lg p-3 ${toneClass}`} data-testid={`insight-${tone}`}>
      <div className="flex items-start gap-2">
        <Icon size={16} className={`${iconColor} mt-0.5 shrink-0`}/>
        <div className="flex-1">
          <p className="text-sm font-semibold">{ins.message}</p>
          <p className="text-[11px] text-slate-600 mt-1">Question: <span className="text-slate-700">{stripHtml(ins.title)}</span></p>
        </div>
      </div>
    </li>
  );
}

function Empty({ msg }) {
  return <div className="h-full min-h-[140px] grid place-items-center text-slate-500 text-sm py-8">{msg}</div>;
}

function pct(n, d) { return d ? Math.round((n / d) * 100) : 0; }

function npsZone(score) {
  if (score >= 70) return "World-class";
  if (score >= 50) return "Excellent";
  if (score >= 30) return "Great";
  if (score >= 0) return "Needs work";
  return "Critical";
}
